import React, { useCallback, useEffect, useReducer, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';
import ComponentPropBuilder from 'Common/Table/ComponentPropBuilder';
import View from './ReadyToCheck.view';
import useGrouping from 'Common/Utils/Hooks/useGrouping';
import { getContracts } from '../utils';
import { fetchReadyAppointments } from '../service';
import errorCb from 'Common/Utils/errorCb';
import initialState from '../store/initialState';
import reducer from '../store/validateContract.reducer';
import Actions from '../store/validateContract.actions';
import schema, { keyField } from '../schema';
import * as Commands from '../CommandButtons/CommandConstants';
import CheckPDF from '../CheckPDF/CheckPDF.container';
import { getExportSchema, getSortFields, sortOrders } from '../exportSchemas';
import { updateAppointments } from 'Features/services/AppointmentUpdate.service';
import dataSort from 'Common/Utils/Sort/dataSort';
import ViewTAndC from '../CheckPDF/ViewTAndC.container';

const ReadyToCheck = () => {
	const grouping = useGrouping();
	const globalDispatch = useDispatch();

	const updateYear = useCallback(year => dispatch({ type: Actions.SET_YEAR, payload: { year } }), []);
	const setAppointments = useCallback(
		(_appointments, _isLoaded) => dispatch({ type: Actions.SET_APPOINTMENTS, payload: { appointments: _appointments, isLoaded: _isLoaded } }),
		[]
	);
	const setContracts = useCallback(contracts => dispatch({ type: Actions.SET_CONTRACTS, payload: { contracts } }), []);
	const onContractSelect = useCallback(currentContract => dispatch({ type: Actions.SET_CURRENT_CONTRACT, payload: { currentContract } }), []);
	const onTeamChange = useCallback(team => dispatch({ type: Actions.SET_TEAM, payload: { team } }), []);
	const onAppointeeChange = useCallback(appointee => dispatch({ type: Actions.SET_APPOINTEE, payload: { appointee } }), []);

	const setValidateSuccess = useCallback(
		(_validationResult, remainingAppointments) =>
			dispatch({
				type: Actions.VALIDATE_SUCCESS,
				payload: { validationResult: _validationResult, appointments: remainingAppointments }
			}),
		[]
	);

	const setValidatePartialSuccess = useCallback(
		(_validationResult, remainingAppointments) =>
			dispatch({
				type: Actions.VALIDATE_PARTIAL_SUCCESS,
				payload: { validationResult: _validationResult, appointments: remainingAppointments }
			}),
		[]
	);

	const setValidateFail = useCallback(validationResult => dispatch({ type: Actions.VALIDATE_FAIL, payload: { validationResult } }), []);

	const [state, dispatch] = useReducer(reducer, initialState);
	const { year, appointments, contracts, currentContract, viewPDF, viewCheckPDF, validationResult, isLoaded, viewTAndC } = state;

	useEffect(() => {
		let didCancel = false;

		if (year) {
			const getReadyAppointments = async () => {
				const _appointments = await fetchReadyAppointments(year, grouping, errorCb(null, 'Unable to update contracts', globalDispatch));
				if (!didCancel && _appointments !== null) {
					setAppointments(_appointments, true);
				}
			};
			getReadyAppointments();
		}
		return () => {
			didCancel = true;
		};
	}, [year, grouping, globalDispatch, setAppointments]);

	useEffect(() => {
		const _contracts = getContracts(appointments, year);
		setContracts(_contracts);
	}, [appointments, year, setContracts]);

	const onCellChange = useCallback(
		(e, field) => {
			const {
				target: { value }
			} = e;
			switch (field) {
				case 'teams':
					onTeamChange(value);
					break;
				case 'appointees':
					onAppointeeChange(value);
					break;
				default:
					break;
			}
		},
		[onTeamChange, onAppointeeChange]
	);

	const validate = async () => {
		const { type, template } = currentContract;
		const appointmentsToUpdate = appointments.filter(appointment => appointment.type === type && appointment.template === template);
		const updateCount = appointmentsToUpdate.length;
		const patch = [
			{
				path: '/ReadyForApproval',
				op: 'replace',
				value: 'Ready To Issue'
			}
		];
		const updateResult = await updateAppointments(year, grouping, appointmentsToUpdate, patch);
		const { updatedAll, updatedIds } = updateResult;
		const remainingAppointments = appointments.filter(appointment => !updatedIds.includes(appointment.appointmentId));

		const suffix = updateCount > 1 ? 's' : '';
		const successMessage = `Updated ${updatedIds.length} appointment${suffix} successfully`;
		const failMessage = `Only updated ${updatedIds.length} out of ${updateCount} appointment${suffix}`;
		const _validationResult = {
			result: updatedAll,
			hasResult: true,
			successMessage,
			failMessage
		};

		if (updatedAll) {
			setValidateSuccess(_validationResult, remainingAppointments);
		} else if (updatedIds.length > 0) {
			setValidatePartialSuccess(_validationResult, remainingAppointments);
		} else {
			setValidateFail(_validationResult);
		}
	};

	const onCellClick = e => {
		const {
			currentTarget: { name: command }
		} = e;
		const { VALIDATE, CHECK, VIEW_PDF, VIEW_TANDC } = Commands;
		switch (command) {
			case VALIDATE:
				validate();
				break;
			case CHECK:
				dispatch({ type: Actions.CHECK });
				break;
			case VIEW_PDF:
				dispatch({ type: Actions.VIEW_PDF });
				break;
			case VIEW_TANDC:
				dispatch({ type: Actions.VIEW_TANDC });
				break;
			default:
				break;
		}
	};

	const handlePDFChecked = () => {
		dispatch({ type: Actions.CHECKED_PDF });
	};

	const handleClosedPDF = () => {
		dispatch({ type: Actions.CLOSED_PDF });
	};

	const getExportData = useCallback(() => {
		const { type, template } = currentContract;
		const exportSchema = getExportSchema(grouping);
		const fields = exportSchema.map(column => column.label);
		const sortFields = getSortFields(grouping);

		const exportData = appointments
			.filter(appointment => appointment.type === type && appointment.template === template)
			.map(appointment => {
				const _appointment = { year, ...appointment };
				return exportSchema.reduce((arr, column) => {
					const value = _get(_appointment, column.field);
					arr.push(value);
					return arr;
				}, []);
			});

		const data = dataSort(fields, exportData, sortFields, sortOrders);
		return { fields, data };
	}, [currentContract, appointments, year, grouping]);

	const fileName = useMemo(() => {
		if (currentContract) {
			const { type, description, template } = currentContract;
			return `Appointments at Ready for ${description} (${type}) - ${template} ${year}.csv`;
		}
		return '';
	}, [year, currentContract]);

	const componentProps = useMemo(() => {
		return new ComponentPropBuilder().withColumnProp('checked', { getExportData }).withColumnProp('checked', { fileName }).getProps();
	}, [getExportData, fileName]);

	const mainTitle = contracts.length ? `Check and Validate ${contracts.length} Contracts ${year}` : 'Check and Validate Contracts';
	document.title = `Contract Management - ${mainTitle}`;

	if (viewCheckPDF || viewPDF) {
		return (
			<CheckPDF
				currentContract={currentContract}
				viewPDF={viewPDF}
				viewCheckPDF={viewCheckPDF}
				handlePDFChecked={handlePDFChecked}
				handleClosedPDF={handleClosedPDF}
				grouping={grouping}
			/>
		);
	}

	if (viewTAndC) {
		return (
			<ViewTAndC
				currentContract={currentContract}
				viewTAndC={viewTAndC}
				handlePDFChecked={handlePDFChecked}
				handleClosedPDF={handleClosedPDF}
				grouping={grouping}
			/>
		);
	}

	return (
		<View
			year={year}
			updateYear={updateYear}
			grouping={grouping}
			contracts={contracts}
			title={mainTitle}
			schema={schema}
			onContractSelect={onContractSelect}
			currentContract={currentContract}
			keyField={keyField}
			onCellChange={onCellChange}
			onCellClick={onCellClick}
			componentProps={componentProps}
			validationResult={validationResult}
			isLoaded={isLoaded}
		/>
	);
};

export default ReadyToCheck;
